import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../services/api';
import { useTranslation } from '../contexts/LanguageContext';

const Settings = () => {
  const { language, changeLanguage, t } = useTranslation();
  const [settings, setSettings] = useState({
    restaurantName: 'Restaurant Admin',
    currency: 'LBP',
    taxRate: '8.5',
    email: 'admin@restaurant.com',
    phone: '+1 234 567 8900',
    address: '123 Restaurant St, City, State 12345',
    openingHours: {
      monday: '9:00 AM - 10:00 PM',
      tuesday: '9:00 AM - 10:00 PM',
      wednesday: '9:00 AM - 10:00 PM',
      thursday: '9:00 AM - 10:00 PM',
      friday: '9:00 AM - 11:00 PM',
      saturday: '9:00 AM - 11:00 PM',
      sunday: '10:00 AM - 9:00 PM'
    },
    notifications: {
      newOrders: true,
      lowStock: true,
      customerReviews: false,
      dailyReports: true
    },
    theme: 'light',
    language: 'en',
    dollarRate: '89000'
  });
  
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchSettings();
    applyTheme();
  }, []);

  useEffect(() => {
    // Update settings language when language changes
    setSettings(prev => ({
      ...prev,
      language: language
    }));
  }, [language]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for language change
    if (name === 'language') {
      changeLanguage(value);
      localStorage.setItem('language', value);
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage('Error loading settings');
    }
  };

  const applyTheme = () => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.body.setAttribute('data-theme', savedTheme);
  };

  const handleThemeChange = (theme) => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await updateSettings(settings);
      setSettings(response);
      
      // Apply theme if changed
      if (settings.theme) {
        handleThemeChange(settings.theme);
      }
      
      setMessage(t('settingsSavedSuccessfully'));
    } catch (error) {
      setMessage(t('errorSavingSettings'));
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm(t('confirmResetSettings'))) {
      const defaultSettings = {
        restaurantName: 'Restaurant Admin',
        currency: 'LBP',
        taxRate: 8.5,
        email: 'admin@restaurant.com',
        phone: '+1 234 567 8900',
        address: '123 Restaurant St, City, State 12345',
        openingHours: {
          monday: '9:00 AM - 10:00 PM',
          tuesday: '9:00 AM - 10:00 PM',
          wednesday: '9:00 AM - 10:00 PM',
          thursday: '9:00 AM - 10:00 PM',
          friday: '9:00 AM - 11:00 PM',
          saturday: '9:00 AM - 11:00 PM',
          sunday: '10:00 AM - 9:00 PM'
        },
        notifications: {
          newOrders: true,
          lowStock: true,
          customerReviews: false,
          dailyReports: true
        },
        theme: 'light',
        language: 'en',
        dollarRate: 89000
      };
      setSettings(defaultSettings);
      handleThemeChange('light');
      setMessage(t('settingsResetToDefault'));
    }
  };

  return (
    <div className="content">
      <h1 className="page-title">{t('settings')}</h1>
      
      {message && (
        <div className={`message ${message.includes('successfully') || message.includes('reset') ? 'message-success' : 'message-error'}`}>
          {message}
        </div>
      )}

      <div className="settings-container">
        <div className="settings-sidebar">
          <button 
            className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('generalSettings')}
          </button>
          <button 
            className={`settings-tab ${activeTab === 'business' ? 'active' : ''}`}
            onClick={() => setActiveTab('business')}
          >
            {t('businessInfo')}
          </button>
          <button 
            className={`settings-tab ${activeTab === 'hours' ? 'active' : ''}`}
            onClick={() => setActiveTab('hours')}
          >
            {t('openingHours')}
          </button>
          <button 
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            {t('notificationPreferences')}
          </button>
          <button 
            className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            {t('appearance')}
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2>{t('generalSettings')}</h2>
              <div className="form-group">
                <label className="form-label">{t('restaurantName')}</label>
                <input
                  type="text"
                  name="restaurantName"
                  value={settings.restaurantName}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">{t('currency')}</label>
                <select
                  name="currency"
                  value={settings.currency}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="USD">USD ($)</option>
                  <option value="LBP">LBP (ل.ل)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="JPY">JPY (¥)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('taxRate')}</label>
                <input
                  type="number"
                  name="taxRate"
                  value={settings.taxRate}
                  onChange={handleInputChange}
                  className="form-input"
                  step="0.1"
                  min="0"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('dollarExchangeRate')}</label>
                <input
                  type="number"
                  name="dollarRate"
                  value={settings.dollarRate}
                  onChange={handleInputChange}
                  className="form-input"
                  step="0.01"
                  min="0"
                  placeholder="Enter current dollar rate"
                />
                <small style={{ color: '#b0b0b0', marginTop: '5px', display: 'block' }}>
                  {t('currentExchangeRate')}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">{t('language')}</label>
                <select
                  name="language"
                  value={settings.language}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'business' && (
            <div className="settings-section">
              <h2>{t('businessInfo')}</h2>
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={settings.phone}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('address')}</label>
                <textarea
                  name="address"
                  value={settings.address}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                />
              </div>
            </div>
          )}

          {activeTab === 'hours' && (
            <div className="settings-section">
              <h2>{t('openingHours')}</h2>
              {Object.entries(settings.openingHours).map(([day, hours]) => (
                <div key={day} className="form-group">
                  <label className="form-label">{day.charAt(0).toUpperCase() + day.slice(1)}</label>
                  <input
                    type="text"
                    name={`openingHours.${day}`}
                    value={hours}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="e.g., 9:00 AM - 10:00 PM"
                  />
                </div>
              ))}
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>{t('notificationPreferences')}</h2>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="notifications.newOrders"
                    checked={settings.notifications.newOrders}
                    onChange={handleInputChange}
                  />
                  <span>{t('newOrders')}</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="notifications.lowStock"
                    checked={settings.notifications.lowStock}
                    onChange={handleInputChange}
                  />
                  <span>{t('lowStock')} Alerts</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="notifications.customerReviews"
                    checked={settings.notifications.customerReviews}
                    onChange={handleInputChange}
                  />
                  <span>{t('customerReviews')}</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="notifications.dailyReports"
                    checked={settings.notifications.dailyReports}
                    onChange={handleInputChange}
                  />
                  <span>{t('dailyReports')}</span>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="settings-section">
              <h2>{t('appearance')}</h2>
              <div className="form-group">
                <label className="form-label">{t('theme')}</label>
                <select
                  name="theme"
                  value={settings.theme}
                  onChange={(e) => {
                    handleInputChange(e);
                    handleThemeChange(e.target.value);
                  }}
                  className="form-select"
                >
                  <option value="light">{t('light')}</option>
                </select>
                <small style={{ color: '#b0b0b0', marginTop: '5px', display: 'block' }}>
                  {t('choosePreferredTheme')}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">{t('primaryColor')}</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value="#ffd700"
                    onChange={(e) => {
                      document.documentElement.style.setProperty('--primary-color', e.target.value);
                    }}
                    style={{ 
                      width: '50px', 
                      height: '40px', 
                      border: 'none', 
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ color: '#b0b0b0' }}>{t('clickToChangePrimaryColor')}</span>
                </div>
              </div>
            </div>
          )}

          <div className="settings-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleSaveSettings}
              disabled={loading}
            >
              {loading ? t('saving') : t('saveSettings')}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleResetSettings}
            >
              {t('resetToDefault')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
